import {
    resolveAttunementAttributePoints,
    resolveSkillPointsEarned,
    SKILL_BRANCH_NAMES,
    VEIL_ATTUNEMENT_MILESTONE_PERCENTS,
    type CharacterDisciplines,
    type SkillBranch,
} from '@sorrem/shared';
import { useMemo } from 'react';
import { useCurrentPlayerCharacter } from '@sorrem-client/ui/hooks/use-current-player-character';
import { SKILL_TREE_MARTIAL_TIERS } from '@sorrem-client/ui/features/SkillTree/skill-tree-model';
import { resolveMartialTierName } from '@sorrem-client/ui/features/SkillTree/skill-tree-details';

export interface BranchMastery {
    readonly id: SkillBranch;
    readonly label: string;
    /** Mastery of this branch, from 0 to 100, or null while the branch has no defined range. */
    readonly masteryPercent: number | null;
    /** Percentages at which the branch grants a reward, marked around its dial. */
    readonly milestones?: readonly number[];
    /** What the current mastery has earned, shown in the dial's tooltip. */
    readonly detail?: string;
}

interface BranchMasteryState {
    readonly branches: readonly BranchMastery[];
}

function resolveVeilAttunementDetail(masteryPercent: number): string {
    const attributePointsAvailable = resolveAttunementAttributePoints(VEIL_ATTUNEMENT_MILESTONE_PERCENTS.at(-1) ?? 0);
    return `${resolveAttunementAttributePoints(masteryPercent)} / ${attributePointsAvailable} attribute points and ${resolveSkillPointsEarned(masteryPercent)} skill points earned`;
}

/** Before a character loads. */
const NO_DISCIPLINES: CharacterDisciplines = { veilAttunement: 0, martialDiscipline: 0 };

/**
 * A character's mastery in each progression branch, from the disciplines
 * the shared rules give (`UIPartyCharacter.disciplines`), as the skill tree
 * and gear requirements read them (#330). Veil Attunement grows with the
 * essence abilities spend (#145); Martial Discipline with Martial skill
 * levels (#262); Crafting has no defined range yet, so it stays null
 * (shown as "not tracked yet").
 */
export function resolveBranchMastery(disciplines: CharacterDisciplines): BranchMastery[] {
    return [
        {
            id: 'essence',
            label: SKILL_BRANCH_NAMES.essence,
            masteryPercent: disciplines.veilAttunement,
            milestones: VEIL_ATTUNEMENT_MILESTONE_PERCENTS,
            detail: resolveVeilAttunementDetail(disciplines.veilAttunement),
        },
        { id: 'crafting', label: SKILL_BRANCH_NAMES.crafting, masteryPercent: null },
        {
            id: 'martial',
            label: SKILL_BRANCH_NAMES.martial,
            masteryPercent: disciplines.martialDiscipline,
            milestones: SKILL_TREE_MARTIAL_TIERS.map((tier) => tier.percent),
            detail: resolveMartialTierName(disciplines.martialDiscipline),
        },
    ];
}

/** Returns the selected character's mastery in each progression branch (`resolveBranchMastery`). */
export function useBranchMastery(): BranchMasteryState {
    const { character } = useCurrentPlayerCharacter();
    const disciplines = character?.disciplines ?? NO_DISCIPLINES;
    return useMemo(() => ({ branches: resolveBranchMastery(disciplines) }), [disciplines]);
}
